import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {useEffect, useState} from 'react'
import Loader from 'app/custom/loader/Loader'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'
import CustomSelect from 'app/custom/CustomSelect'
import {PlanningAcademicYears} from '../../../redux/Slice/generalSlice'
import {useAuth} from 'app/modules/auth'
import {StudentStatistic} from '../../../redux/Slice/studentSlice'

interface GenderData {
  male?: number
  female?: number
}

interface AvgAgeData {
  program_level: string
  avg_age: number | string
}

interface ProgramData {
  program_name: string
  student_count: number
}

interface AttendanceData {
  status_label: string
  student_count: number
}

interface SponsorData {
  label: string
  total: number
}

const StdStatistics = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const {currentUser} = useAuth()
  const [loader, setLoader] = useState(true)
  const [academicYears, setAcademicYears] = useState<any[]>([])
  const [selectedAy, setSelectedAy] = useState<number[]>([])
  const [studentGender, setStudentGender] = useState<Record<string, GenderData>>({})
  const [studentAvgAge, setStudentAvgAge] = useState<Record<string, AvgAgeData[]>>({})
  const [studentByProgram, setStudentByProgram] = useState<Record<string, ProgramData[]>>({})
  const [studentByAcademicYear, setStudentByAcademicYear] = useState<Record<string, any>>({})
  const [studentAttendance, setStudentAttendance] = useState<Record<string, AttendanceData[]>>({})
  const [studentSponsor, setStudentSponsor] = useState<Record<string, SponsorData[]>>({})

  const fetchData = (ayIds: any[]) => {
    setLoader(true)
    const fd = new FormData()
    ayIds.forEach((id) => fd.append('academic_year_id[]', id))

    dispatch(StudentStatistic(fd)).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStudentGender(res.payload.studentGender || {})
        setStudentAvgAge(res.payload.studentAvgAge || {})
        setStudentByProgram(res.payload.studentByProgram || {})
        setStudentByAcademicYear(res.payload.studentByAcademicYear || {})
        setStudentAttendance(res.payload.studentAttendance || {})
        setStudentSponsor(res.payload.sponsors || {})
        setLoader(false)
      }
    })
  }

  useEffect(() => {
    dispatch(PlanningAcademicYears()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        const years = res.payload
        setAcademicYears(years)
        const sorted = [...years].sort((a: any, b: any) => a.name.localeCompare(b.name))
        const lastFour = sorted.slice(-4).map((y) => y.id)
        setSelectedAy(lastFour)
        fetchData(lastFour)
      }
    })
  }, [])

  const handleSelectAll = () => {
    setSelectedAy(academicYears.map((ay: any) => ay.id))
  }

  const yearLabels = Object.keys(studentGender)

  const blackText = {
    style: {
      colors: ['#000'],
      fontSize: '12px',
      fontWeight: 'bold',
    },
  }

  const maleSeries = yearLabels.map((year) => {
    const g = studentGender[year] || {}
    const total = (g.male || 0) + (g.female || 0)
    return total ? Number((((g.male || 0) / total) * 100).toFixed(2)) : 0
  })

  const femaleSeries = yearLabels.map((year) => {
    const g = studentGender[year] || {}
    const total = (g.male || 0) + (g.female || 0)
    return total ? Number((((g.female || 0) / total) * 100).toFixed(2)) : 0
  })

  const genderSeries = [
    {name: 'Male', data: maleSeries},
    {name: 'Female', data: femaleSeries},
  ]

  const genderOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {categories: yearLabels, labels: blackText},
    yaxis: {max: 100, labels: {...blackText, formatter: (val) => `${val}%`}},
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: {colors: ['#000']},
      offsetY: -20,
    },
    colors: ['#4A90E2', '#FF6EC7'],
    legend: {position: 'bottom', labels: {colors: ['#000']}},
  }

  const programNames = Array.from(
    new Set(
      Object.values(studentByProgram)
        .flat()
        .map((p) => p.program_name)
    )
  )

  const programSeries = programNames.map((program) => ({
    name: program,
    data: yearLabels.map((year) => {
      const list = studentByProgram[year] || []
      const total = list.reduce((s, x) => s + x.student_count, 0)
      const count = list.find((x) => x.program_name === program)?.student_count || 0
      return total ? Number(((count / total) * 100).toFixed(2)) : 0
    }),
  }))

  const programOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {categories: yearLabels, labels: blackText},
    yaxis: {max: 100, labels: {...blackText, formatter: (val) => `${val}%`}},
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: {colors: ['#000']},
      offsetY: -20,
    },
    legend: {position: 'bottom', labels: {colors: ['#000']}},
  }

  const attendanceTypes = Array.from(
    new Set(
      Object.values(studentAttendance)
        .flat()
        .map((a) => a.status_label)
    )
  )

  const attendanceSeries = attendanceTypes.map((status) => ({
    name: status,
    data: yearLabels.map((year) => {
      const list = studentAttendance[year] || []
      const total = list.reduce((s, x) => s + x.student_count, 0)
      const count = list.find((x) => x.status_label === status)?.student_count || 0
      return total ? Number(((count / total) * 100).toFixed(2)) : 0
    }),
  }))

  const attendanceOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {categories: yearLabels, labels: blackText},
    yaxis: {max: 100, labels: {...blackText, formatter: (val) => `${val}%`}},
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: {colors: ['#000']},
      offsetY: -20,
    },
    legend: {position: 'bottom', labels: {colors: ['#000']}},
  }

  const sponsorNames = Array.from(
    new Set(
      Object.values(studentSponsor)
        .flat()
        .map((s) => s.label)
    )
  )

  const sponsorSeries = sponsorNames.map((label) => ({
    name: label,
    data: yearLabels.map((year) => {
      const list = studentSponsor[year] || []
      const total = list.reduce((s, x) => s + x.total, 0)
      const count = list.find((x) => x.label === label)?.total || 0
      return total ? Number(((count / total) * 100).toFixed(2)) : 0
    }),
  }))

  const sponsorOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {categories: yearLabels, labels: blackText},
    yaxis: {max: 100, labels: {...blackText, formatter: (val) => `${val}%`}},
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: {colors: ['#000']},
      offsetY: -20,
    },
    legend: {position: 'bottom', labels: {colors: ['#000']}},
  }

  const totalYearCount = Object.values(studentByAcademicYear).reduce(
    (s, x) => s + (x.student_count || 0),
    0
  )

  const yearCountSeries = [
    {
      name: 'Students',
      data: yearLabels.map((year) => {
        const c = studentByAcademicYear[year]?.student_count || 0
        return totalYearCount ? Number(((c / totalYearCount) * 100).toFixed()) : 0
      }),
    },
  ]

  const yearCountOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        distributed: true,
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {categories: yearLabels, labels: blackText},
    yaxis: {max: 100, labels: {...blackText, formatter: (val) => `${val}%`}},

    dataLabels: {
      enabled: true,
      formatter: (val) => `${val}%`,
      style: {colors: ['#000']},
      offsetY: -20,
    },

    // 🔥 MULTIPLE COLORS FOR EACH SERIES/BAR
    colors: [
      '#4A90E2', // blue
      '#50C878', // green
      '#F5A623', // orange
      '#D0021B', // red
      '#9B59B6', // purple
      '#F39C12', // amber
      '#1ABC9C', // teal
      '#E74C3C', // scarlet
      '#2ECC71', // soft green
      '#3498DB', // sky blue
    ],
  }

  const programLevels = Array.from(
    new Set(
      Object.values(studentAvgAge)
        .flat()
        .map((p) => p.program_level)
    )
  )

  const avgAgeSeries = programLevels.map((program) => ({
    name: program,
    data: yearLabels.map((year) => {
      const row = studentAvgAge[year]?.find((x) => x.program_level === program)
      if (!row || row.avg_age == null) return 0
      const num = typeof row.avg_age === 'number' ? row.avg_age : parseFloat(row.avg_age)
      if (isNaN(num)) return 0
      return parseFloat(num.toFixed(1))
    }),
  }))

  const avgAgeOptions: ApexOptions = {
    chart: {type: 'bar', height: 350},
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top',
        },
      },
    },
    xaxis: {
      categories: yearLabels,
      labels: blackText,
    },
    yaxis: {
      labels: {
        ...blackText,
        formatter: (val) => `${val} yrs`,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${val} yrs`,
      style: {colors: ['#000']},
      offsetY: -20,
    },
    legend: {position: 'bottom', labels: {colors: ['#000']}},
    colors: ['#4A90E2', '#50C878', '#F5A623', '#D0021B', '#8E44AD', '#E67E22'],
  }

  return (
    <>
      <div className='card mb-5'>
        <div className='card-body'>
          <div className='row'>
            <div className='col-md-10'>
              <label className='form-label fw-bold mb-1'>
                {t('LABEL.ACADEMIC_YEAR')}
                <span
                  className='mx-3 text-primary'
                  style={{cursor: 'pointer'}}
                  onClick={handleSelectAll}
                >
                  {t('GLOBAL.SELECT_ALL')}
                </span>
              </label>

              <CustomSelect
                isMulti
                options={academicYears}
                value={academicYears.filter((a) => selectedAy.includes(a.id))}
                onChange={(items) => setSelectedAy(items.map((i: any) => i.id))}
              />
            </div>

            <div className='col-md-2 pt-7'>
              <button
                className='btn btn-outline-primary btn-sm fw-bold px-4 w-100'
                onClick={() => selectedAy.length > 0 && fetchData(selectedAy)}
              >
                <span className='fa fa-search mx-2'></span>
                {t('GLOBAL.SEARCH')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {loader && <Loader />}

      {!loader && (
        <div className='row'>
          <div className='col-12 mb-5'>
            <div className='card shadow-sm'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>{t('DASHBOARD.STUDENT_GENDER')}</h5>
                <ReactApexChart
                  options={genderOptions}
                  series={genderSeries}
                  type='bar'
                  height={350}
                />
              </div>
            </div>
          </div>

          <div className='col-12 mb-5'>
            <div className='card shadow-sm'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>{t('DASHBOARD.STD_PROGRAM')}</h5>
                <ReactApexChart
                  options={programOptions}
                  series={programSeries}
                  type='bar'
                  height={350}
                />
              </div>
            </div>
          </div>

          <div className='col-12 mb-5'>
            <div className='card shadow-sm'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>{t('DASHBOARD.STD_ATT')}</h5>
                <ReactApexChart
                  options={attendanceOptions}
                  series={attendanceSeries}
                  type='bar'
                  height={350}
                />
              </div>
            </div>
          </div>

          {currentUser?.campus_id !== 1 && (
            <div className='col-12 mb-5'>
              <div className='card shadow-sm'>
                <div className='card-body'>
                  <h5 className='text-center mb-3'>{t('DASHBOARD.STD_SPONSOR')}</h5>
                  <ReactApexChart
                    options={sponsorOptions}
                    series={sponsorSeries}
                    type='bar'
                    height={350}
                  />
                </div>
              </div>
            </div>
          )}

          <div className='col-12 mb-5'>
            <div className='card shadow-sm'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>{t('DASHBOARD.STD_AY')}</h5>
                <ReactApexChart
                  options={yearCountOptions}
                  series={yearCountSeries}
                  type='bar'
                  height={350}
                />
              </div>
            </div>
          </div>

          <div className='col-12 mb-5'>
            <div className='card shadow-sm'>
              <div className='card-body'>
                <h5 className='text-center mb-3'>{t('DASHBOARD.AVG_AGE')}</h5>
                <ReactApexChart
                  options={avgAgeOptions}
                  series={avgAgeSeries}
                  type='bar'
                  height={350}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default StdStatistics
